import { AnalyticsBrowser } from "@segment/analytics-next";
import { shouldUseNewAnalytics } from "./feature-flags";

// ============================================================================
// Document Type Sanitisation
// ============================================================================
// Strips redundant generator-name phrases from AI-generated TYPE values so
// analytics / lead notifications read e.g. "loto air compressor" instead of
// "loto loto procedure for air compressor".

const GENERATOR_REDUNDANT_PATTERNS: Record<string, RegExp[]> = {
  loto: [
    /\blockout\s*[/-]?\s*tag\s*out\b/i,
    /\bloto\b/i,
    /\blockout\s+tagout\b/i,
    /\block\s*out\s*[/-]?\s*tag\s*out\b/i,
  ],
  puwer: [
    /\bpuwer\s+assessment\b/i,
    /\bpuwer\b/i,
    /\bwork\s+equipment\s+assessment\b/i,
  ],
  rams: [
    /\brisk\s+assessment\s+(?:and\s+)?method\s+statement\b/i,
    /\brams\b/i,
    /\bmethod\s+statement\b/i,
  ],
  swms: [
    /\bsafe\s+work\s+method\s+statement\b/i,
    /\bswms\b/i,
  ],
  jsa: [/\bjob\s+safety\s+analysis\b/i, /\bjsa\b/i],
  jha: [/\bjob\s+hazard\s+analysis\b/i, /\bjha\b/i],
  jtsa: [/\bjob\s+task\s+safety\s+analysis\b/i, /\bjtsa\b/i],
  flra: [/\bfield\s+level\s+risk\s+assessment\b/i, /\bflra\b/i],
  flha: [/\bfield\s+level\s+hazard\s+assessment\b/i, /\bflha\b/i],
  aha: [/\bactivity\s+hazard\s+analysis\b/i, /\baha\b/i],
  lmra: [/\blast\s+minute\s+risk\s+assessment\b/i, /\blmra\b/i],
  sop: [/\bsafe\s+operating\s+procedure\b/i, /\bsop\b/i],
  swp: [/\bsafe\s+work\s+p(?:rocedure|ack)\b/i, /\bswp\b/i],
  ptw: [/\bpermit\s+to\s+work\b/i, /\bptw\b/i],
  ptb: [/\bpre[\s-]?task\s+briefing\b/i, /\bptb\b/i],
  ptp: [/\bpre[\s-]?task\s+plan(?:ning)?\b/i, /\bptp\b/i],
  itp: [/\binspection\s+(?:and\s+)?test\s+plan\b/i, /\bitp\b/i],
  itc: [/\binspection\s+(?:and\s+)?test\s+checklist\b/i, /\bitc\b/i],
  itr: [/\binspection\s+(?:and\s+)?test\s+re(?:cord|port)\b/i, /\bitr\b/i],
  loler: [/\bloler\s+assessment\b/i, /\bloler\b/i],
  sssp: [/\bsite[\s-]?specific\s+safety\s+plan\b/i, /\bsssp\b/i],
  pra: [/\bproject\s+risk\s+assessment\b/i, /\bpra\b/i],
  scl: [/\bsafety\s+checklist\b/i, /\bsite\s+inspection\s+checklist\b/i, /\bscl\b/i],
  ecl: [/\benvironmental\s+checklist\b/i, /\becl\b/i],
  qac: [/\bquality\s+(?:assurance\s+)?checklist\b/i, /\bqac\b/i],
  qmp: [/\bquality\s+management\s+plan\b/i, /\bqmp\b/i],
  cmp: [/\bconstruction\s+management\s+plan\b/i, /\bcmp\b/i],
  smp: [/\bsafety\s+management\s+plan\b/i, /\bsmp\b/i],
  voc: [/\bverification\s+of\s+competency\b/i, /\bvoc\b/i],
  take5: [/\btake\s*5\b/i],
  tailgate: [/\btailgate\s*(?:talk|meeting)?\b/i],
  toolbox: [/\btoolbox\s*talk\b/i],
  induction: [/\binduction\b/i],
  inspection: [/\binspection\s+(?:report|checklist)\b/i, /\binspection\b/i],
  'safety-meeting': [/\bsafety\s+meeting\b/i, /\btoolbox\s+safety\s+meeting\b/i],
  coshh: [/\bcoshh\s+assessment\b/i, /\bcontrol\s+of\s+substances\s+hazardous\s+to\s+health\b/i, /\bcoshh\b/i],
  pssr: [/\bpre[\s-]?start(?:up)?\s+safety\s+review\b/i, /\bpssr\b/i],
  dra: [/\bdesktop\s+risk\s+assessment\b/i, /\bdra\b/i],
};

/**
 * Universal wrapper-phrase patterns stripped from ALL generator TYPE values.
 * These catch common AI echo patterns like "Create a Safe Operating Procedure for X".
 */
const UNIVERSAL_WRAPPER_PATTERNS: RegExp[] = [
  // Conversational prefixes: "i need a RAMS for ...", "can you make a JSA for ..."
  /^(?:please\s+)?(?:i\s+need|i\s+want|can\s+you\s+(?:make|create|generate|write|draft)|i(?:'d|\s+would)\s+like)\s+(?:a\s+|an\s+)?/i,
  // "Create/Generate/Write a [document type] for ..."
  /^(?:please\s+)?(?:generate|create|write|draft|prepare|produce|develop|make|build)\s+(?:a\s+|an\s+|me\s+a\s+|me\s+an\s+)?/i,
  // Document-type phrases that may linger after the verb strip
  /^(?:safe\s+operating\s+procedure|safe\s+work\s+(?:method\s+statement|procedure|pack)|sop|swms|rams|jha|jsa|jtsa|flra|flha|aha|lmra|ptb|ptp|ptw|puwer|loler|scl|ecl|qac|qmp|cmp|smp|itp|itc|itr|swp|take\s*5|toolbox\s*talk|tailgate\s*(?:talk|meeting)?|safety\s+(?:checklist|meeting)|inspection\s+(?:report|checklist)|(?:risk\s+)?assessment|(?:hazard\s+)?analysis|verification\s+of\s+competency|(?:method\s+)?statement|voc|induction|quality\s+management\s+plan|construction\s+management\s+plan|safety\s+management\s+plan|coshh\s+assessment|control\s+of\s+substances\s+hazardous\s+to\s+health|pre[\s-]?start(?:up)?\s+safety\s+review|coshh|pssr)\s*/i,
  // "checklist for", "procedure for", "document for/about/covering", "report on"
  /^(?:checklist|procedure|document|report|plan|assessment|analysis|briefing|talk|meeting|pack|statement)\s+(?:for|about|covering|on|of)\s+/i,
  // Lingering prepositions at the start
  /^\s*(?:for|about|covering|on|of)\s+/i,
];

/** Maximum word count for a clean TYPE value */
const MAX_TYPE_WORDS = 8;
const CLAUSE_DELIMITER_PATTERN = /[,:;\n]+/;

export interface DocumentTypeResolutionInput {
  generatorSlug: string;
  rawType?: string | null;
  generatedActivity?: string | null;
  submittedActivity?: string | null;
}

interface LeadCreatedDocumentInput {
  swmsType?: string;
  rawType?: string | null;
  generatedActivity?: string | null;
  submittedActivity?: string | null;
}

function normalizeDocumentTypeWhitespace(value: string): string {
  return value.replace(/\s{2,}/g, ' ').trim();
}

function stripTrailingDocumentTypePunctuation(value: string): string {
  return value.replace(/[.,:;!?]+$/, '').trim();
}

function truncateAtSentenceBoundary(value: string): string {
  const firstSentence = value.match(/^[^.!?]+/);
  return firstSentence ? firstSentence[0].trim() : value;
}

function truncateAtClauseBoundary(value: string): string {
  const [firstClause] = value.split(CLAUSE_DELIMITER_PATTERN);
  return (firstClause || value).trim();
}

function truncateDocumentTypeWords(value: string, maxWords = MAX_TYPE_WORDS): string {
  const words = value.split(/\s+/).filter(Boolean);
  return words.length > maxWords ? words.slice(0, maxWords).join(' ') : value;
}

function fallbackSanitizeDocumentType(rawType: string): string | undefined {
  const cleaned = stripTrailingDocumentTypePunctuation(
    normalizeDocumentTypeWhitespace(
      truncateDocumentTypeWords(
        truncateAtClauseBoundary(
          truncateAtSentenceBoundary(rawType),
        ),
      ),
    ),
  );

  return cleaned ? toTitleCase(cleaned) : undefined;
}

function stripDocumentTypeBoilerplate(rawType: string, generatorSlug: string): string {
  let cleaned = rawType;

  const patterns = GENERATOR_REDUNDANT_PATTERNS[generatorSlug];
  if (patterns) {
    for (const pattern of patterns) {
      cleaned = cleaned.replace(pattern, '');
    }
  }

  for (let i = 0; i < 2; i++) {
    for (const pattern of UNIVERSAL_WRAPPER_PATTERNS) {
      cleaned = cleaned.replace(pattern, '');
    }
  }

  cleaned = cleaned.replace(/^\s*(procedure|process|program|document)\s+(for|of|on)\s+/i, '');
  cleaned = cleaned.replace(/^\s*(for|of|on)\s+/i, '');
  cleaned = normalizeDocumentTypeWhitespace(cleaned);
  cleaned = truncateAtSentenceBoundary(cleaned);
  cleaned = truncateAtClauseBoundary(cleaned);
  cleaned = stripTrailingDocumentTypePunctuation(normalizeDocumentTypeWhitespace(cleaned));

  return truncateDocumentTypeWords(cleaned);
}

/**
 * Remove redundant generator-name phrases from a document TYPE string,
 * strip universal wrapper prefixes, and truncate to a concise category.
 *
 * e.g. "loto procedure for air compressor" → "Air Compressor"
 *      "Create a Safety Checklist for scaffolding inspection at height" → "Scaffolding Inspection at Height"
 *      "Lockout Tag Out of brewhouse..." → "Brewhouse..."
 */
export function cleanDocumentType(rawType: string | null | undefined, generatorSlug: string): string | undefined {
  if (!rawType?.trim()) return undefined;

  const cleaned = stripDocumentTypeBoilerplate(rawType, generatorSlug);
  if (!cleaned) return undefined;
  // Guard: reject degenerate residues (pure digits, single tokens <3 chars,
  // or lone punctuation) that survive aggressive boilerplate stripping —
  // e.g. "PRA 1" collapsing to "1" after /\bpra\b/i is removed.
  const compact = cleaned.replace(/[^A-Za-z0-9]/g, '');
  if (compact.length < 3) return undefined;
  if (/^\d+$/.test(compact)) return undefined;
  return toTitleCase(cleaned);
}

function sanitizeTrackedDocumentType(rawType: string | null | undefined, generatorSlug: string): string | undefined {
  if (!rawType?.trim()) return undefined;

  return cleanDocumentType(rawType, generatorSlug) ?? fallbackSanitizeDocumentType(rawType);
}

function resolveLeadCreatedDocumentType(
  generatorSlug: string,
  {
    swmsType,
    rawType,
    generatedActivity,
    submittedActivity,
  }: LeadCreatedDocumentInput,
): string | undefined {
  return (
    resolveDocumentType({
      generatorSlug,
      rawType,
      generatedActivity,
      submittedActivity,
    }) ?? sanitizeTrackedDocumentType(swmsType, generatorSlug)
  );
}

/**
 * Resolve the analytics/lead-capture document type from the best available source.
 * Priority: explicit TYPE → generated ACTIVITY → submitted activity.
 */
export function resolveDocumentType({
  generatorSlug,
  rawType,
  generatedActivity,
  submittedActivity,
}: DocumentTypeResolutionInput): string | undefined {
  for (const candidate of [rawType, generatedActivity, submittedActivity]) {
    const cleaned = cleanDocumentType(candidate, generatorSlug);
    if (cleaned) {
      return cleaned;
    }
  }

  return undefined;
}

/** Convert a string to Title Case, preserving common acronyms */
function toTitleCase(str: string): string {
  const ACRONYMS = new Set(['PPE', 'HVAC', 'CNC', 'LED', 'AC', 'DC', 'UV', 'OH&S', 'WHS', 'OHS', 'HSE', 'OSHA', 'EPA', 'ISO']);
  return str.replace(/\b\w+/g, (word) => {
    const upper = word.toUpperCase();
    if (ACRONYMS.has(upper)) return upper;
    if (word.length <= 2 && !['a', 'i'].includes(word.toLowerCase())) return word; // keep short words as-is
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
}

const writeKey = import.meta.env.VITE_SEGMENT_WRITE_KEY;
const isDevMode = import.meta.env.DEV;

// ============================================================================
// Generator Slug Cache
// ============================================================================
// Cache the generator slug so analytics functions don't need it as a parameter
// Set once when the app loads via setGeneratorSlug() from useDomainGenerator hook

let cachedGeneratorSlug: string | null = null;
let cachedGeneratorDisplayName: string | null = null;

/**
 * Set the current generator identity (called once when app initializes).
 * displayName is the human-readable product name from the generators table
 * (e.g. "Safety Checklist Generator") used in Salesforce-bound traits.
 */
export const setGeneratorIdentity = (slug: string, displayName?: string | null) => {
  cachedGeneratorSlug = slug;
  cachedGeneratorDisplayName = displayName ?? null;
};

/**
 * Back-compat shim: prefer setGeneratorIdentity(slug, displayName).
 */
export const setGeneratorSlug = (slug: string) => {
  setGeneratorIdentity(slug, null);
};

/**
 * Get the current generator slug from cache
 * @returns The cached generator slug, or 'swms' as fallback
 */
const getCurrentGeneratorSlug = (): string => {
  return cachedGeneratorSlug || "swms";
};

/**
 * Get the human-readable product name for the current generator.
 * Falls back to a Title-Cased "<SLUG> Generator" if the display name
 * was not provided (legacy callers / dev override).
 */
const getCurrentGeneratorDisplayName = (): string => {
  if (cachedGeneratorDisplayName) return cachedGeneratorDisplayName;
  const slug = getCurrentGeneratorSlug();
  return `${slug.toUpperCase()} Generator`;
};

const isDevEnvironment = () => {
  const hostname = window.location.hostname;
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.includes("lovable.app") ||
    hostname.includes("lovable.dev") ||
    hostname.includes(".local") ||
    isDevMode
  );
};

/**
 * Debug logger for analytics events in dev environments
 * Shows what would be tracked even when Segment is disabled
 */
const logAnalyticsDebug = (
  version: "V1 (Legacy)" | "V2 (New)",
  eventName: string,
  properties: Record<string, any>,
  identifyData?: Record<string, any>,
) => {
  if (isDevEnvironment()) {
    const style =
      version === "V1 (Legacy)"
        ? "background: #ff6b6b; color: white; padding: 2px 6px; border-radius: 3px;"
        : "background: #51cf66; color: white; padding: 2px 6px; border-radius: 3px;";

    console.group(`%c${version}%c ${eventName}`, style, "color: #228be6; font-weight: bold;");
    console.log("Properties:", properties);
    if (identifyData) {
      console.log("Identify Data:", identifyData);
    }
    console.log("Would fire in production:", !isDevEnvironment());
    console.groupEnd();
  }
};

// Disable analytics in development mode or when no write key is provided
export const analytics = AnalyticsBrowser.load(
  { writeKey: writeKey || "" },
  { disable: isDevEnvironment() || !writeKey },
);

// Event context enum for cleaner tracking
export enum EventContext {
  SWMS_GENERATOR = "SWMS Generator",
  GENERATE = "Generate",
  DOWNLOAD = "Download",
  FEEDBACK_SUBMITTED = "Feedback Submitted",
  COPY_WEBSITE_LINK = "Copy Website Link",
  GENERATE_ANOTHER_SWMS = "Generate Another SWMS",
}

// Session tracking for "Prompt Added" - only fires once per browser session
let hasTrackedPromptAdded = false;

// Map country codes to continent codes
const countryToContinentMap: Record<string, string> = {
  AU: "OC",
  NZ: "OC",
  US: "NA",
  CA: "NA",
  GB: "EU",
  DE: "EU",
  FR: "EU",
  JP: "AS",
  CN: "AS",
  IN: "AS",
  BR: "SA",
  ZA: "AF",
};

// Get continent code from user-selected country (preferred) or timezone fallback
const getContinentCode = (country: string): string => {
  // First: Use the user-selected country if it's in our mapping
  // This is more accurate because the user explicitly chose their jurisdiction
  if (country && countryToContinentMap[country]) {
    return countryToContinentMap[country];
  }

  // Second: Fallback to browser timezone for unknown countries
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone) {
      const region = timezone.split("/")[0];
      const timezoneToContinent: Record<string, string> = {
        Africa: "AF",
        America: "NA",
        Antarctica: "AN",
        Asia: "AS",
        Atlantic: "EU",
        Australia: "OC",
        Europe: "EU",
        Indian: "AS",
        Pacific: "OC",
      };
      if (timezoneToContinent[region]) {
        return timezoneToContinent[region];
      }
    }
  } catch {
    // Fallback to default
  }
  
  // Default to Oceania (original default, maintains backward compatibility)
  return "OC";
};

// UTM parameter types
export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  ref?: string;
}

// Get UTM parameters from URL
export const getUtmParams = (): UtmParams => {
  const params = new URLSearchParams(window.location.search);
  const utmParams: UtmParams = {};

  const utm_source = params.get("utm_source");
  const utm_medium = params.get("utm_medium");
  const utm_campaign = params.get("utm_campaign");
  const utm_term = params.get("utm_term");
  const utm_content = params.get("utm_content");
  const ref = params.get("ref");

  if (utm_source) utmParams.utm_source = utm_source;
  if (utm_medium) utmParams.utm_medium = utm_medium;
  if (utm_campaign) utmParams.utm_campaign = utm_campaign;
  if (utm_term) utmParams.utm_term = utm_term;
  if (utm_content) utmParams.utm_content = utm_content;
  if (ref) utmParams.ref = ref;

  return utmParams;
};

// 1. Page Visited → Context: SWMS Generator
export const trackPageVisited = () => {
  const generatorSlug = getCurrentGeneratorSlug();

  if (shouldUseNewAnalytics(generatorSlug)) {
    trackPageVisitedV2(generatorSlug);
    return;
  }

  // Legacy analytics (SWMS only)
  const properties = { Context: EventContext.SWMS_GENERATOR };
  logAnalyticsDebug("V1 (Legacy)", "Page Visited", properties);
  analytics.track("Page Visited", properties);
};

// 2. Prompt Added (only fire once per session)
export const trackPromptAdded = () => {
  if (hasTrackedPromptAdded) return;

  const generatorSlug = getCurrentGeneratorSlug();

  if (shouldUseNewAnalytics(generatorSlug)) {
    trackPromptAddedV2(generatorSlug);
    return;
  }

  // Legacy analytics (SWMS only) - set flag after routing
  hasTrackedPromptAdded = true;
  const properties = {};
  logAnalyticsDebug("V1 (Legacy)", "Prompt Added", properties);
  analytics.track("Prompt Added");
};

// 3. Button Clicked → with Context (with optional feedback traits for FEEDBACK_SUBMITTED)
export const trackButtonClicked = (
  context: EventContext,
  feedbackTraits?: {
    rating?: number;
    referralSource?: string;
    comments?: string;
    swmsType?: string;
  },
) => {
  const generatorSlug = getCurrentGeneratorSlug();
  const safeSwmsType = sanitizeTrackedDocumentType(feedbackTraits?.swmsType, generatorSlug);

  if (shouldUseNewAnalytics(generatorSlug)) {
    // Map old EventContext to V2 context string
    const contextMap: Record<EventContext, string> = {
      [EventContext.SWMS_GENERATOR]: EventContextV2.GENERATOR,
      [EventContext.GENERATE]: EventContextV2.GENERATE,
      [EventContext.DOWNLOAD]: EventContextV2.DOWNLOAD,
      [EventContext.FEEDBACK_SUBMITTED]: EventContextV2.FEEDBACK_SUBMITTED,
      [EventContext.COPY_WEBSITE_LINK]: EventContextV2.COPY_WEBSITE_LINK,
      [EventContext.GENERATE_ANOTHER_SWMS]: EventContextV2.GENERATE_ANOTHER,
    };

    trackButtonClickedV2(contextMap[context] as any, generatorSlug, {
      rating: feedbackTraits?.rating,
      referralSource: feedbackTraits?.referralSource,
      comments: feedbackTraits?.comments,
      documentType: safeSwmsType,
    });
    return;
  }

  // Legacy analytics (SWMS only)
  const properties = {
    Context: context,
    ...(feedbackTraits?.rating !== undefined && {
      rating: feedbackTraits.rating,
    }),
    ...(feedbackTraits?.referralSource && {
      referralSource: feedbackTraits.referralSource,
    }),
    ...(feedbackTraits?.comments && { comments: feedbackTraits.comments }),
    ...(safeSwmsType && { swms_type: safeSwmsType }),
  };
  logAnalyticsDebug("V1 (Legacy)", "Button Clicked", properties);
  analytics.track("Button Clicked", properties);
};

// 4. Email Capture Form Modal
export const trackEmailCaptureFormModal = (options?: {
  model?: string;
  durationSeconds?: number;
  swmsType?: string;
}) => {
  const generatorSlug = getCurrentGeneratorSlug();
  const safeSwmsType = sanitizeTrackedDocumentType(options?.swmsType, generatorSlug);

  if (shouldUseNewAnalytics(generatorSlug)) {
    trackEmailCaptureFormModalV2({
      generatorSlug,
      documentType: safeSwmsType,
      model: options?.model,
      durationSeconds: options?.durationSeconds,
    });
    return;
  }

  // Legacy analytics (SWMS only)
  const properties = {
    ...(options?.model && { model: options.model }),
    ...(options?.durationSeconds !== undefined && {
      generation_duration_seconds: options.durationSeconds,
    }),
    ...(safeSwmsType && { swms_type: safeSwmsType }),
  };
  logAnalyticsDebug("V1 (Legacy)", "Email Capture Form Modal", properties);
  analytics.track("Email Capture Form Modal", properties);
};

// 6. File Successfully Downloaded
export const trackFileSuccessfullyDownloaded = (swmsType?: string) => {
  const generatorSlug = getCurrentGeneratorSlug();
  const safeSwmsType = sanitizeTrackedDocumentType(swmsType, generatorSlug);

  if (shouldUseNewAnalytics(generatorSlug)) {
    trackFileSuccessfullyDownloadedV2({
      generatorSlug,
      documentType: safeSwmsType,
    });
    return;
  }

  // Legacy analytics (SWMS only)
  const properties = {
    ...(safeSwmsType && { swms_type: safeSwmsType }),
  };
  logAnalyticsDebug("V1 (Legacy)", "File Successfully Downloaded", properties);
  analytics.track("File Successfully Downloaded", properties);
};

// 7. Lead Created (track + identify with UTM params)
export const trackLeadCreated = ({
  email,
  phone,
  firstName,
  lastName,
  country,
  swmsType,
  rawType,
  generatedActivity,
  submittedActivity,
}: {
  email: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  country: string;
  swmsType?: string;
  rawType?: string | null;
  generatedActivity?: string | null;
  submittedActivity?: string | null;
}) => {
  const generatorSlug = getCurrentGeneratorSlug();

  if (shouldUseNewAnalytics(generatorSlug)) {
    trackLeadCreatedV2({
      generatorSlug,
      documentType: swmsType,
      rawType,
      generatedActivity,
      submittedActivity,
      email,
      phone,
      firstName,
      lastName,
      country,
    });
    return;
  }

  const safeSwmsType = resolveLeadCreatedDocumentType(generatorSlug, {
    swmsType,
    rawType,
    generatedActivity,
    submittedActivity,
  });

  // Legacy analytics (SWMS only)
  const namePart = email.split("@")[0];
  const continentCode = getContinentCode(country);
  const utmParams = getUtmParams();

  // Identify with required traits + UTM params
  const identifyData = {
    email,
    ...(phone && { phone }),
    firstName: firstName ?? namePart,
    lastName: lastName ?? namePart,
    company: { name: `${getCurrentGeneratorDisplayName()} – ${email}` },
    continentCode,
    swms_type: safeSwmsType,
    ...utmParams,
  };

  const trackProperties = {
    ...(safeSwmsType && { swms_type: safeSwmsType }),
  };

  logAnalyticsDebug("V1 (Legacy)", "Lead Created", trackProperties, identifyData);

  analytics.identify(email, identifyData);
  analytics.track("Lead Created", trackProperties);
};

// 8. Download Complete Modal
export const trackDownloadCompleteModal = () => {
  const generatorSlug = getCurrentGeneratorSlug();

  if (shouldUseNewAnalytics(generatorSlug)) {
    trackDownloadCompleteModalV2(generatorSlug);
    return;
  }

  // Legacy analytics (SWMS only)
  const properties = {};
  logAnalyticsDebug("V1 (Legacy)", "Download Complete Modal", properties);
  analytics.track("Download Complete Modal");
};

// 9. User Submitted Rating
export const trackUserSubmittedRating = (rating: number, swmsType?: string) => {
  const generatorSlug = getCurrentGeneratorSlug();
  const safeSwmsType = sanitizeTrackedDocumentType(swmsType, generatorSlug);

  if (shouldUseNewAnalytics(generatorSlug)) {
    trackUserSubmittedRatingV2({
      generatorSlug,
      documentType: safeSwmsType,
      rating,
    });
    return;
  }

  // Legacy analytics (SWMS only)
  const properties = {
    Context: rating.toString(),
    ...(safeSwmsType && { swms_type: safeSwmsType }),
  };
  logAnalyticsDebug("V1 (Legacy)", "User Submitted Rating", properties);
  analytics.track("User Submitted Rating", properties);
};

// 10. User Submitted Referral Source
export const trackUserSubmittedReferralSource = (source: string, swmsType?: string) => {
  const generatorSlug = getCurrentGeneratorSlug();
  const safeSwmsType = sanitizeTrackedDocumentType(swmsType, generatorSlug);

  if (shouldUseNewAnalytics(generatorSlug)) {
    trackUserSubmittedReferralSourceV2({
      generatorSlug,
      documentType: safeSwmsType,
      source,
    });
    return;
  }

  // Legacy analytics (SWMS only)
  const properties = {
    Context: source,
    ...(safeSwmsType && { swms_type: safeSwmsType }),
  };
  logAnalyticsDebug("V1 (Legacy)", "User Submitted Referral Source", properties);
  analytics.track("User Submitted Referral Source", properties);
};

// 11. Feedback Commented
export const trackFeedbackCommented = (swmsType?: string) => {
  const generatorSlug = getCurrentGeneratorSlug();
  const safeSwmsType = sanitizeTrackedDocumentType(swmsType, generatorSlug);

  if (shouldUseNewAnalytics(generatorSlug)) {
    trackFeedbackCommentedV2({
      generatorSlug,
      documentType: safeSwmsType,
    });
    return;
  }

  // Legacy analytics (SWMS only)
  const properties = {
    ...(safeSwmsType && { swms_type: safeSwmsType }),
  };
  logAnalyticsDebug("V1 (Legacy)", "Feedback Commented", properties);
  analytics.track("Feedback Commented", properties);
};

// ============================================================================
// V2 Analytics Functions (Domain-Driven)
// ============================================================================
// These functions support multi-generator tracking with generator_slug + document_type
// Legacy functions above are for SWMS during transition period only

export enum EventContextV2 {
  GENERATOR = "Generator",
  GENERATE = "Generate",
  DOWNLOAD = "Download",
  FEEDBACK_SUBMITTED = "Feedback Submitted",
  COPY_WEBSITE_LINK = "Copy Website Link",
  GENERATE_ANOTHER = "Generate Another",
}

export const trackPageVisitedV2 = (generatorSlug: string) => {
  const context = getCurrentGeneratorDisplayName();
  const properties = {
    Context: context,
    generator_slug: generatorSlug,
  };
  logAnalyticsDebug("V2 (New)", "Page Visited", properties);
  analytics.track("Page Visited", properties);
};

export const trackPromptAddedV2 = (generatorSlug: string) => {
  if (hasTrackedPromptAdded) return;
  hasTrackedPromptAdded = true;

  const properties = {
    generator_slug: generatorSlug,
  };
  logAnalyticsDebug("V2 (New)", "Prompt Added", properties);
  analytics.track("Prompt Added", properties);
};

export const trackButtonClickedV2 = (
  context: EventContextV2,
  generatorSlug: string,
  feedbackTraits?: {
    rating?: number;
    referralSource?: string;
    comments?: string;
    documentType?: string;
  },
) => {
  const safeDocumentType = sanitizeTrackedDocumentType(feedbackTraits?.documentType, generatorSlug);

  const properties = {
    Context: context,
    generator_slug: generatorSlug,
    ...(feedbackTraits?.rating !== undefined && {
      rating: feedbackTraits.rating,
    }),
    ...(feedbackTraits?.referralSource && {
      referralSource: feedbackTraits.referralSource,
    }),
    ...(feedbackTraits?.comments && { comments: feedbackTraits.comments }),
    ...(safeDocumentType && {
      document_type: safeDocumentType,
    }),
  };
  logAnalyticsDebug("V2 (New)", "Button Clicked", properties);
  analytics.track("Button Clicked", properties);
};

export const trackEmailCaptureFormModalV2 = (params: {
  generatorSlug: string;
  documentType?: string;
  model?: string;
  durationSeconds?: number;
}) => {
  const safeDocumentType = sanitizeTrackedDocumentType(params.documentType, params.generatorSlug);

  const properties = {
    generator_slug: params.generatorSlug,
    ...(safeDocumentType && { document_type: safeDocumentType }),
    ...(params.model && { model: params.model }),
    ...(params.durationSeconds !== undefined && {
      generation_duration_seconds: params.durationSeconds,
    }),
  };
  logAnalyticsDebug("V2 (New)", "Email Capture Form Modal", properties);
  analytics.track("Email Capture Form Modal", properties);
};

export const trackFileSuccessfullyDownloadedV2 = (params: { generatorSlug: string; documentType?: string }) => {
  const safeDocumentType = sanitizeTrackedDocumentType(params.documentType, params.generatorSlug);

  const properties = {
    generator_slug: params.generatorSlug,
    ...(safeDocumentType && { document_type: safeDocumentType }),
  };
  logAnalyticsDebug("V2 (New)", "File Successfully Downloaded", properties);
  analytics.track("File Successfully Downloaded", properties);
};

export const trackLeadCreatedV2 = (params: {
  generatorSlug: string;
  documentType?: string;
  rawType?: string | null;
  generatedActivity?: string | null;
  submittedActivity?: string | null;
  email: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  country: string;
}) => {
  const safeDocumentType = resolveLeadCreatedDocumentType(params.generatorSlug, {
    swmsType: params.documentType,
    rawType: params.rawType,
    generatedActivity: params.generatedActivity,
    submittedActivity: params.submittedActivity,
  });

  const namePart = params.email.split("@")[0];
  const continentCode = getContinentCode(params.country);
  const utmParams = getUtmParams();
  const displayName = getCurrentGeneratorDisplayName();

  const identifyData = {
    Context: "Generator",
    email: params.email,
    ...(params.phone && { phone: params.phone }),
    firstName: params.firstName ?? namePart,
    lastName: params.lastName ?? namePart,
    company: { name: `${displayName} – ${params.email}` },
    continentCode,
    generator_slug: params.generatorSlug,
    ...(safeDocumentType && { document_type: safeDocumentType }),
    // Backward-compat: Segment Slack destination reads swms_type
    ...(safeDocumentType && { swms_type: safeDocumentType }),
    ...utmParams,
  };

  const trackProperties = {
    generator_slug: params.generatorSlug,
    ...(safeDocumentType && { document_type: safeDocumentType }),
    // Backward-compat: Segment Slack destination reads swms_type
    ...(safeDocumentType && { swms_type: safeDocumentType }),
  };

  logAnalyticsDebug("V2 (New)", "Lead Created", trackProperties, identifyData);

  analytics.identify(params.email, identifyData);
  analytics.track("Lead Created", trackProperties);
};

export const trackDownloadCompleteModalV2 = (generatorSlug: string) => {
  const properties = {
    generator_slug: generatorSlug,
  };
  logAnalyticsDebug("V2 (New)", "Download Complete Modal", properties);
  analytics.track("Download Complete Modal", properties);
};

export const trackUserSubmittedRatingV2 = (params: {
  generatorSlug: string;
  documentType?: string;
  rating: number;
}) => {
  const safeDocumentType = sanitizeTrackedDocumentType(params.documentType, params.generatorSlug);

  const properties = {
    Context: params.rating.toString(),
    generator_slug: params.generatorSlug,
    ...(safeDocumentType && { document_type: safeDocumentType }),
  };
  logAnalyticsDebug("V2 (New)", "User Submitted Rating", properties);
  analytics.track("User Submitted Rating", properties);
};

export const trackUserSubmittedReferralSourceV2 = (params: {
  generatorSlug: string;
  documentType?: string;
  source: string;
}) => {
  const safeDocumentType = sanitizeTrackedDocumentType(params.documentType, params.generatorSlug);

  const properties = {
    Context: params.source,
    generator_slug: params.generatorSlug,
    ...(safeDocumentType && { document_type: safeDocumentType }),
  };
  logAnalyticsDebug("V2 (New)", "User Submitted Referral Source", properties);
  analytics.track("User Submitted Referral Source", properties);
};

export const trackFeedbackCommentedV2 = (params: { generatorSlug: string; documentType?: string }) => {
  const safeDocumentType = sanitizeTrackedDocumentType(params.documentType, params.generatorSlug);

  const properties = {
    generator_slug: params.generatorSlug,
    ...(safeDocumentType && { document_type: safeDocumentType }),
  };
  logAnalyticsDebug("V2 (New)", "Feedback Commented", properties);
  analytics.track("Feedback Commented", properties);
};

/**
 * Attachment uploaded — fired when a user adds a photo or plan via the Tools popover.
 * Tracks per-generator engagement with the multimodal "classify-then-extract" flow.
 *
 * - surface: "web" (in-app uploads via ChatInterface) or "api" (future: api-generate consumers)
 * - variant_key: A/B layout variant (A = stacked, B = split-preview)
 * - file_kind: "photo" or "plan"
 */
export const trackAttachmentUploadedV2 = (params: {
  generatorSlug: string;
  fileKind: "photo" | "plan";
  fileCount: number;
  totalAttachments: number;
  surface?: "web" | "api";
  variantKey?: string | null;
}) => {
  const properties = {
    generator_slug: params.generatorSlug,
    file_kind: params.fileKind,
    file_count: params.fileCount,
    total_attachments: params.totalAttachments,
    surface: params.surface ?? "web",
    ...(params.variantKey && { variant_key: params.variantKey }),
  };
  logAnalyticsDebug("V2 (New)", "Attachment Uploaded", properties);
  analytics.track("Attachment Uploaded", properties);
};
